import { useEffect, useState } from 'react';
import { Download, Share, X, Bell } from 'lucide-react';
import { isStandalone, pushSupported, enablePush } from '../pwa';

// Evento beforeinstallprompt (no está en los tipos estándar de TS).
type BIPEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

const DISMISS_KEY = 'lm-install-dismissed';
const PUSH_DISMISS_KEY = 'lm-push-dismissed';
const REASK_MS = 14 * 24 * 60 * 60 * 1000; // re-ofrecer a las 2 semanas

function dismissedRecently(key: string): boolean {
  try {
    const t = Number(localStorage.getItem(key) || 0);
    return t > 0 && Date.now() - t < REASK_MS;
  } catch { return false; }
}

function isIosSafari(): boolean {
  const ua = navigator.userAgent;
  const iOS = /iphone|ipad|ipod/i.test(ua);
  const webkit = /webkit/i.test(ua);
  const notChromeOrFx = !/crios|fxios|edgios/i.test(ua);
  return iOS && webkit && notChromeOrFx;
}

/**
 * Banner discreto (solo móvil) para:
 *  1) instalar la app (Chrome/Android vía beforeinstallprompt, iOS vía hint),
 *  2) una vez instalada, activar las notificaciones push.
 * El escritorio no se ve afectado (oculto en ≥sm).
 */
export default function InstallPrompt() {
  const [deferred, setDeferred] = useState<BIPEvent | null>(null);
  const [showInstall, setShowInstall] = useState(false);
  const [showIos, setShowIos] = useState(false);
  const [showPush, setShowPush] = useState(false);

  useEffect(() => {
    if (isStandalone()) {
      // Ya instalada → ofrecer notificaciones si aún no decidió.
      if (
        pushSupported() &&
        Notification.permission === 'default' &&
        !dismissedRecently(PUSH_DISMISS_KEY)
      ) {
        setShowPush(true);
      }
      return;
    }

    // No instalada → banner de instalación.
    if (dismissedRecently(DISMISS_KEY)) return;

    const onBIP = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BIPEvent);
      setShowInstall(true);
    };
    window.addEventListener('beforeinstallprompt', onBIP);

    const onInstalled = () => { setShowInstall(false); setDeferred(null); };
    window.addEventListener('appinstalled', onInstalled);

    // iOS no dispara beforeinstallprompt → mostramos un hint manual.
    if (isIosSafari()) setShowIos(true);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBIP);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  async function doInstall() {
    if (!deferred) return;
    await deferred.prompt();
    await deferred.userChoice.catch(() => null);
    setShowInstall(false);
    setDeferred(null);
  }

  function dismiss(key: string, hide: () => void) {
    try { localStorage.setItem(key, String(Date.now())); } catch { /* noop */ }
    hide();
  }

  async function doEnablePush() {
    const ok = await enablePush();
    setShowPush(false);
    if (!ok) { try { localStorage.setItem(PUSH_DISMISS_KEY, String(Date.now())); } catch { /* noop */ } }
  }

  // z-[35]: por encima de la barra inferior (z-30) pero por debajo de los
  // overlays (off-canvas / sheet del FAB / modales, z-40+) para que estos lo cubran.
  const wrap =
    'fixed inset-x-3 bottom-[calc(4.5rem+env(safe-area-inset-bottom))] z-[35] mx-auto max-w-md ' +
    'rounded-2xl border border-line bg-surface p-4 shadow-2xl sm:hidden';

  if (showPush) {
    return (
      <div className={wrap}>
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent/15 text-accent-ink">
            <Bell size={20} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-content">Activa los avisos</p>
            <p className="mt-0.5 text-xs text-content-3">Recibe recordatorios de pagos, clases y mensajes aunque la app esté cerrada.</p>
            <div className="mt-3 flex gap-2">
              <button onClick={doEnablePush} className="flex-1 rounded-lg bg-accent px-3 py-2 text-sm font-semibold text-on-accent">Activar</button>
              <button onClick={() => dismiss(PUSH_DISMISS_KEY, () => setShowPush(false))} className="rounded-lg px-3 py-2 text-sm font-medium text-content-3">Ahora no</button>
            </div>
          </div>
          <button onClick={() => dismiss(PUSH_DISMISS_KEY, () => setShowPush(false))} aria-label="Cerrar" className="text-content-3"><X size={18} /></button>
        </div>
      </div>
    );
  }

  if (showInstall && deferred) {
    return (
      <div className={wrap}>
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent/15 text-accent-ink">
            <Download size={20} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-content">Instala Let's Move</p>
            <p className="mt-0.5 text-xs text-content-3">Acceso directo en tu pantalla de inicio, pantalla completa y más rápido.</p>
            <div className="mt-3 flex gap-2">
              <button onClick={doInstall} className="flex-1 rounded-lg bg-accent px-3 py-2 text-sm font-semibold text-on-accent">Instalar</button>
              <button onClick={() => dismiss(DISMISS_KEY, () => setShowInstall(false))} className="rounded-lg px-3 py-2 text-sm font-medium text-content-3">Ahora no</button>
            </div>
          </div>
          <button onClick={() => dismiss(DISMISS_KEY, () => setShowInstall(false))} aria-label="Cerrar" className="text-content-3"><X size={18} /></button>
        </div>
      </div>
    );
  }

  if (showIos) {
    return (
      <div className={wrap}>
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent/15 text-accent-ink">
            <Share size={20} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-content">Agrega Let's Move a inicio</p>
            <p className="mt-0.5 text-xs text-content-3">Toca <Share size={12} className="inline -mt-0.5" /> <b>Compartir</b> y luego <b>«Agregar a inicio»</b>.</p>
          </div>
          <button onClick={() => dismiss(DISMISS_KEY, () => setShowIos(false))} aria-label="Cerrar" className="text-content-3"><X size={18} /></button>
        </div>
      </div>
    );
  }

  return null;
}
